"use client";

import { useRef, useState } from "react";
import Link from "next/link";
import {
  LuStar,
  LuMapPin,
  LuBookOpen,
  LuBuilding2,
  LuUsers,
  LuCalendar,
  LuMessageCircle,
} from "react-icons/lu";
import type { Agency } from "@/hooks/useAgencies";
import { useAuthStore } from "@/store/useAuthStore";
import { ComposePopover } from "./ComposePopover";

// ── Shared sub-components ────────────────────────────────────────────────────

function StarRow({ rating, reviews }: { rating: number; reviews: number }) {
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((s) => (
        <LuStar
          key={s}
          className={`w-3 h-3 ${
            s <= Math.round(rating)
              ? "text-amber-400 fill-amber-400"
              : "text-gray-300 dark:text-gray-600"
          }`}
        />
      ))}
      <span className="ml-1 text-xs text-gray-500 dark:text-gray-400">
        {(rating ?? 0).toFixed(1)}
        {reviews > 0 && <> ({reviews})</>}
      </span>
    </div>
  );
}

function Chip({
  color,
  icon: Icon,
  label,
}: {
  color: "indigo" | "purple";
  icon: React.ElementType;
  label: string;
}) {
  const cls =
    color === "indigo"
      ? "bg-indigo-50 dark:bg-indigo-500/10 text-indigo-700 dark:text-indigo-300 border-indigo-100 dark:border-indigo-500/20"
      : "bg-purple-50 dark:bg-purple-500/10 text-purple-700 dark:text-purple-300 border-purple-100 dark:border-purple-500/20";
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-xs font-medium max-w-[130px] min-w-0 ${cls}`}
    >
      <Icon className="w-2.5 h-2.5 shrink-0" />
      <span className="truncate">{label}</span>
    </span>
  );
}

function OverflowBadge({ n }: { n: number }) {
  return (
    <span className="px-2 py-0.5 rounded-full bg-gray-100 dark:bg-white/5 text-gray-500 dark:text-gray-400 text-xs shrink-0">
      +{n}
    </span>
  );
}

// ── AgencyCard ───────────────────────────────────────────────────────────────

export type AgencyCardProps = {
  agency: Agency;
  showTypeBadge?: boolean;
};

export function AgencyCard({ agency, showTypeBadge = false }: AgencyCardProps) {
  const { activeRole, accessToken } = useAuthStore();
  const isStudent = !activeRole || activeRole === "STUDENT";
  const canMessage = !!accessToken && isStudent && !!agency.userId;

  const [anchorRect, setAnchorRect] = useState<DOMRect | null>(null);
  const msgBtnRef = useRef<HTMLButtonElement>(null);

  const initials = agency.name
    ? agency.name
        .split(" ")
        .map((n) => n[0])
        .filter(Boolean)
        .join("")
        .toUpperCase()
        .slice(0, 2)
    : "A";

  const countries = Array.isArray(agency.countriesCovered)
    ? agency.countriesCovered
    : [];
  const fields = Array.isArray(agency.fieldsCovered)
    ? agency.fieldsCovered
    : [];
  const services = Array.isArray(agency.servicesOffered)
    ? agency.servicesOffered
    : [];

  return (
    <>
    <Link
      href={`/agencies/${agency.id}`}
      className="group flex flex-col gap-3 p-5 rounded-2xl bg-white/80 dark:bg-white/5 border border-gray-200 dark:border-white/10 hover:border-teal-300 dark:hover:border-teal-500/40 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg dark:hover:shadow-teal-500/5 min-w-0"
    >
      {/* ── Type badge (in flow, above header) ──────────────────────── */}
      {showTypeBadge && (
        <div className="flex justify-end mb-1">
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-teal-50 dark:bg-teal-500/15 text-teal-700 dark:text-teal-300 text-[10px] font-semibold border border-teal-100 dark:border-teal-500/25 uppercase tracking-wide">
            <LuBuilding2 className="w-2.5 h-2.5" />
            Agency
          </span>
        </div>
      )}

      {/* ── Header: logo + name ──────────────────────────────────────── */}
      <div className="flex items-center gap-3 min-w-0">
        <div className="shrink-0">
          {agency.logo ? (
            // eslint-disable-next-line @next/next/no-img-element
            <img
              src={agency.logo}
              alt={agency.name}
              className="w-11 h-11 rounded-xl object-cover border-2 border-gray-200 dark:border-white/15 bg-white"
            />
          ) : (
            <div className="w-11 h-11 rounded-xl bg-teal-100 dark:bg-teal-500/20 flex items-center justify-center text-teal-600 dark:text-teal-400 font-bold text-sm border-2 border-teal-200 dark:border-teal-500/30">
              {initials}
            </div>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 min-w-0">
            <h3 className="font-semibold text-sm text-gray-900 dark:text-white group-hover:text-teal-600 dark:group-hover:text-teal-400 transition-colors truncate leading-snug">
              {agency.name}
            </h3>
            {canMessage && (
              <button
                ref={msgBtnRef}
                type="button"
                aria-label={`Message ${agency.name}`}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (anchorRect) {
                    setAnchorRect(null);
                  } else {
                    const rect = msgBtnRef.current?.getBoundingClientRect();
                    if (rect) setAnchorRect(rect);
                  }
                }}
                className={`shrink-0 flex items-center justify-center w-7 h-7 rounded-lg border transition-colors ${
                  anchorRect
                    ? "bg-teal-600 border-teal-600 text-white"
                    : "text-teal-500 dark:text-teal-400 hover:text-teal-700 dark:hover:text-teal-200 hover:bg-teal-50 dark:hover:bg-teal-500/15 border-teal-200 dark:border-teal-500/30"
                }`}
              >
                <LuMessageCircle className="w-4 h-4" />
              </button>
            )}
          </div>
          <StarRow
            rating={agency.rating ?? 0}
            reviews={agency.totalReviews ?? 0}
          />
        </div>
      </div>

      {/* ── Countries covered ────────────────────────────────────────── */}
      {countries.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {countries.slice(0, 2).map((c) => (
            <Chip key={c} color="indigo" icon={LuMapPin} label={c} />
          ))}
          {countries.length > 2 && <OverflowBadge n={countries.length - 2} />}
        </div>
      )}

      {/* ── Description ─────────────────────────────────────────────── */}
      {agency.description && (
        <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-2 leading-relaxed">
          {agency.description}
        </p>
      )}

      {/* ── Fields covered ───────────────────────────────────────────── */}
      {fields.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {fields.slice(0, 2).map((f) => (
            <Chip key={f} color="purple" icon={LuBookOpen} label={f} />
          ))}
          {fields.length > 2 && <OverflowBadge n={fields.length - 2} />}
        </div>
      )}

      {/* ── Footer ──────────────────────────────────────────────────── */}
      <div className="pt-3 border-t border-gray-100 dark:border-white/8 flex items-center justify-between gap-2">
        <div className="flex items-center gap-3 text-xs text-gray-500 dark:text-gray-400 min-w-0">
          {agency.teamSize != null && (
            <span className="inline-flex items-center gap-1 shrink-0">
              <LuUsers className="w-3.5 h-3.5 shrink-0" />
              {agency.teamSize}
            </span>
          )}
          {agency.yearFounded != null && (
            <span className="inline-flex items-center gap-1 shrink-0">
              <LuCalendar className="w-3.5 h-3.5 shrink-0" />
              Est. {agency.yearFounded}
            </span>
          )}
        </div>
        {services.length > 0 && (
          <span className="shrink-0 inline-flex items-center px-2 py-0.5 rounded-full bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 text-xs font-medium border border-emerald-100 dark:border-emerald-500/20">
            {services.length} service{services.length === 1 ? "" : "s"}
          </span>
        )}
      </div>
    </Link>

    {anchorRect && canMessage && agency.userId && (
      <ComposePopover
        expertUserId={agency.userId}
        expertName={agency.name}
        anchorRect={anchorRect}
        onClose={() => setAnchorRect(null)}
      />
    )}
    </>
  );
}

// ── AgencySkeletonCard ───────────────────────────────────────────────────────

export function AgencySkeletonCard() {
  return (
    <div className="flex flex-col gap-3 p-5 rounded-2xl bg-white/80 dark:bg-white/5 border border-gray-200 dark:border-white/10 animate-pulse">
      <div className="flex items-center gap-3">
        <div className="w-11 h-11 rounded-xl bg-gray-200 dark:bg-gray-700 shrink-0" />
        <div className="flex-1 space-y-2">
          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4" />
          <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-24" />
        </div>
      </div>
      <div className="flex gap-1.5">
        {[1, 2].map((i) => (
          <div
            key={i}
            className="h-5 w-20 bg-gray-200 dark:bg-gray-700 rounded-full"
          />
        ))}
      </div>
      <div className="space-y-1.5">
        <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded" />
        <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-5/6" />
      </div>
      <div className="flex gap-1.5">
        {[1, 2].map((i) => (
          <div
            key={i}
            className="h-5 w-24 bg-gray-200 dark:bg-gray-700 rounded-full"
          />
        ))}
      </div>
      <div className="pt-3 border-t border-gray-100 dark:border-white/8 flex items-center justify-between">
        <div className="h-3 w-20 bg-gray-200 dark:bg-gray-700 rounded" />
        <div className="h-5 w-16 bg-gray-200 dark:bg-gray-700 rounded-full" />
      </div>
    </div>
  );
}
